import { FC, useState, useEffect, useRef } from 'react';
import { useStore, observer } from 'base/store';
import { getEggs, getReward } from '@pro/common/EggHunt/service';
import { IEgg, IReward } from '@pro/common/EggHunt/interface';
import { LoopRender } from '@base/snippets/LoopRender';
import { Egg } from './Egg';
import { createPortal } from 'react-dom';
import { RewardPopup } from '@pro/common/EggHunt/RewardPopup';
import { FlushRecords } from '@pro/common/EggHunt/FlushRecords';
import LoginModal from '@app/common/LoginModal';
import useLogin from 'base/utils/useLogin';
import { useRouter } from 'next/router';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import FixedContainer from '@base/lib/FixedContainer';
import { isPhone } from 'base/utils/client';
import Message from '@base/lib/Message';
import { getWindowWidth, getWindowHeight } from 'ava-util/dist/dom-window';

type IEggHuntProps = {
    id?: string;
}

function checkEggs(eggs: IEgg[]): IEgg[] {
    return eggs.map(egg => {
        const { x, y, width, height } = egg;
        const windowW = isPhone ? 375 : getWindowWidth();
        const bodyH = document.body.offsetHeight;
        const minX = -(width / 2);
        const minY = -(height / 2);
        const maxX = windowW - (width / 2);
        const maxY = bodyH - (height / 2);

        return {
            ...egg,
            active: (
                x >= minX &&
                x <= maxX &&
                y >= minY &&
                y <= maxY
            )
        }
    });
}

export const EggHunt: FC<IEggHuntProps> = observer(({ id }) => {
    const { rootStore, miniCartStore } = useStore();
    const { pageCode } = rootStore.state;

    const { query } = useRouter();
    const { loginModalProps, checkAuth } = useLogin();

    const [eggs, setEggs] = useState<IEgg[]>([]);
    const [reward, setReward] = useState<IReward>();
    const [rewardVisible, setRewardVisible] = useState<boolean>(false);
    const [coordinate, setCoordinate] = useState<[number, number]>();
    const eggsRef = useRef<IEgg[]>([]);

    const fetchData = () => {
        getEggs(pageCode, id).then(res => {
            const _eggs = checkEggs(res);
            setEggs(_eggs);
            eggsRef.current = _eggs;
            handleResize();
        });
    };

    const clickEgg = async () => {
        try {
            const res = await getReward(pageCode, id);
            setReward(res);
            setRewardVisible(true);
            setEggs([]);
            eggsRef.current = [];
            removeEvent(window, 'resize', handleResize);
            if (res.type === 'variant') {
                miniCartStore.afterAddCart(1, [], false);
            }
        } catch (e) {
            Message.error(e.message);
            fetchData();
        }
    }

    const showCoordinate = (e) => {
        if (isPhone) {
            setCoordinate([Math.round(e.touches[0].pageX), Math.round(e.touches[0].pageY)]);
        } else {
            setCoordinate([Math.round(e.pageX), Math.round(e.pageY)]);
        }
    }

    const handleResize = () => {
        const _eggs = eggsRef.current;
        if (_eggs.length) {
            setEggs(checkEggs(_eggs));
        }
    };

    useEffect(() => {
        fetchData();

        if (query['debug_egg'] === '1') {
            addEvent(document.body, isPhone ? 'touchstart' : 'mousemove', showCoordinate);
            return () => {
                removeEvent(document.body, isPhone ? 'touchstart' : 'mousemove', showCoordinate);
            }
        }

        addEvent(window, 'resize', handleResize);

        return () => {
            removeEvent(window, 'resize', handleResize);
        };
    }, []);

    return (
        <>
            {!eggs?.length ? null : createPortal(
                <>
                    <LoopRender
                        tag={false}
                        list={eggs}
                        render={egg => !egg.active ? null : (
                            <Egg
                                egg={egg}
                                onClick={() => checkAuth(clickEgg)}
                            />
                        )}
                    />
                </>,
                document.body
            )}

            <LoginModal
                {...loginModalProps}
                onLoginSuccess={clickEgg}
            />

            {!!reward && (
                <RewardPopup
                    open={rewardVisible}
                    onCancel={() => {
                        setRewardVisible(false);
                    }}
                    reward={reward}
                />
            )}

            {!!coordinate && (
                <FixedContainer className={'site-space bg-white py-2'} width={'auto'}>
                    X: {coordinate[0]} Y: {coordinate[1]}
                </FixedContainer>
            )}

            <FlushRecords fetchData={fetchData}/>
        </>
    );
})
